import { useMemo, useState } from 'react';
import { T, Var, useGT } from 'gt-react';
import { CaretDownIcon, CaretUpIcon } from '@phosphor-icons/react';
import type { KernelTelemetryEnvelope, KernelTelemetrySummary } from '@erp/rpc';
import { Button } from 'src/components/ui/button';
import { Switch } from 'src/components/ui/switch';
import { Label } from 'src/components/ui/label';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'src/components/ui/collapsible';
import { cn } from 'src/lib/utils';
import { useKernelTelemetryEvents } from 'src/api/rpc/hooks/agent-definitions/useKernelTelemetry';
import {
  filterKernelTelemetryDisplay,
  formatKernelTelemetryCollapsedCount,
  formatKernelTelemetryListCount,
  formatNotableTelemetryKind,
  kernelTelemetryEventDetail,
  kernelTelemetryEventTime,
  notableKernelTelemetryKinds,
  type KernelTelemetryTranslate,
} from 'src/components/common/kernelTelemetryDisplay';

const CATEGORY_ORDER = ['control', 'connection', 'system', 'captcha'] as const;

export function KernelTelemetryEventsPanel({
  agentRunId,
  live,
  summary: timelineSummary,
  enabled,
}: {
  agentRunId: string | null;
  live: boolean;
  summary: KernelTelemetrySummary | null;
  enabled: boolean;
}) {
  const panel = useKernelTelemetryPanelState({
    agentRunId,
    live,
    timelineSummary,
    enabled,
  });
  return (
    <Collapsible open={panel.open} onOpenChange={panel.setOpen}>
      <div className="rounded border">
        <KernelTelemetryEventsHeader
          open={panel.open}
          summary={panel.summary}
          gt={panel.gt}
        />
        <CollapsibleContent>
          <KernelTelemetryEventsBody panel={panel} />
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

function useKernelTelemetryPanelState({
  agentRunId,
  live,
  timelineSummary,
  enabled,
}: {
  agentRunId: string | null;
  live: boolean;
  timelineSummary: KernelTelemetrySummary | null;
  enabled: boolean;
}) {
  const gt = useGT();
  const [open, setOpen] = useState(false);
  const [showPointerPath, setShowPointerPath] = useState(false);
  const [category, setCategory] = useState<string | null>(null);
  const [expandedSeq, setExpandedSeq] = useState<number | null>(null);
  const page = useKernelTelemetryEvents({
    agentRunId,
    enabled,
    live,
    category: category ?? undefined,
  });
  const summary = page.summary ?? timelineSummary;
  const visible = useMemo(
    () =>
      filterKernelTelemetryDisplay(page.events, {
        showPointerPath,
        categories: category ? [category] : undefined,
      }),
    [page.events, showPointerPath, category],
  );
  return {
    gt,
    open,
    setOpen,
    showPointerPath,
    setShowPointerPath,
    category,
    setCategory,
    expandedSeq,
    setExpandedSeq,
    page,
    summary,
    visible,
    hiddenPointerCount: page.events.length - visible.length,
  };
}

type KernelTelemetryPanelState = ReturnType<
  typeof useKernelTelemetryPanelState
>;

function KernelTelemetryEventsHeader({
  open,
  summary,
  gt,
}: {
  open: boolean;
  summary: KernelTelemetrySummary | null;
  gt: KernelTelemetryTranslate;
}) {
  return (
    <>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm"
        >
          <span className="font-medium">
            <T context="heading for kernel browser telemetry events list">
              Browser events
            </T>
          </span>
          {open ? (
            <CaretUpIcon className="size-4 shrink-0" />
          ) : (
            <CaretDownIcon className="size-4 shrink-0" />
          )}
        </button>
      </CollapsibleTrigger>
      <KernelTelemetryCollapsedSummary open={open} summary={summary} gt={gt} />
    </>
  );
}

function KernelTelemetryCollapsedSummary({
  open,
  summary,
  gt,
}: {
  open: boolean;
  summary: KernelTelemetrySummary | null;
  gt: KernelTelemetryTranslate;
}) {
  const notableKinds = notableKernelTelemetryKinds(summary);
  return (
    <div className="space-y-1 px-3 pb-3 text-xs text-muted-foreground">
      <p>
        {summary
          ? formatKernelTelemetryCollapsedCount(gt, summary)
          : gt('Captured at Kernel. Retained here after the session ends.', {
              context:
                'kernel telemetry empty summary while loading or unavailable',
            })}
      </p>
      {!open && notableKinds.length > 0 && (
        <p>
          {notableKinds
            .map((item) => formatNotableTelemetryKind(gt, item))
            .join('  ·  ')}
        </p>
      )}
    </div>
  );
}

function KernelTelemetryEventsBody({
  panel,
}: {
  panel: KernelTelemetryPanelState;
}) {
  if (panel.page.loading) {
    return (
      <div className="space-y-3 border-t px-3 py-3">
        <p className="text-sm text-muted-foreground">
          <T context="loading kernel telemetry events">Loading events…</T>
        </p>
      </div>
    );
  }
  if (panel.visible.length === 0 && !panel.summary) {
    return (
      <div className="space-y-3 border-t px-3 py-3">
        <div className="space-y-1 text-sm text-muted-foreground">
          <p>
            <T context="empty state when a run has no kernel telemetry">
              No Kernel telemetry for this run.
            </T>
          </p>
          <p>
            <T context="explanation when kernel telemetry is missing">
              No browser session, or the 30-day Kernel window elapsed before
              persist.
            </T>
          </p>
        </div>
      </div>
    );
  }
  return <KernelTelemetryEventsList panel={panel} />;
}

function KernelTelemetryEventsList({
  panel,
}: {
  panel: KernelTelemetryPanelState;
}) {
  return (
    <div className="space-y-3 border-t px-3 py-3">
      <p className="text-xs text-muted-foreground">
        {formatKernelTelemetryListCount(panel.gt, {
          summary: panel.summary,
          shown: panel.visible.length,
          hiddenPointerCount: panel.hiddenPointerCount,
          showPointerPath: panel.showPointerPath,
        })}
      </p>
      <CategoryFilters
        summary={panel.summary}
        selected={panel.category}
        onSelect={panel.setCategory}
      />
      <div className="flex items-center gap-2">
        <Switch
          id="kernel-telemetry-pointer-path"
          checked={panel.showPointerPath}
          onCheckedChange={panel.setShowPointerPath}
        />
        <Label htmlFor="kernel-telemetry-pointer-path">
          <T context="toggle to show high-volume mouse and screenshot CDP events">
            Show pointer path
          </T>
        </Label>
      </div>
      <ul className="divide-y rounded border">
        {panel.visible.map((envelope) => (
          <KernelTelemetryEventRow
            key={envelope.seq}
            envelope={envelope}
            expanded={panel.expandedSeq === envelope.seq}
            onToggle={() =>
              panel.setExpandedSeq((current) =>
                current === envelope.seq ? null : envelope.seq,
              )
            }
          />
        ))}
      </ul>
      {panel.page.nextCursor && (
        <Button
          variant="outline"
          size="sm"
          disabled={panel.page.loadingMore}
          onClick={() => void panel.page.loadMore()}
        >
          <T context="load more kernel telemetry events">Load more</T>
        </Button>
      )}
    </div>
  );
}

function CategoryFilters({
  summary,
  selected,
  onSelect,
}: {
  summary: KernelTelemetrySummary | null;
  selected: string | null;
  onSelect: (category: string | null) => void;
}) {
  const total = summary?.eventCount ?? 0;
  const categories = CATEGORY_ORDER.filter(
    (category) => (summary?.byCategory[category] ?? 0) > 0,
  );
  return (
    <div className="flex flex-wrap gap-1.5">
      <Button
        type="button"
        size="sm"
        variant={selected === null ? 'secondary' : 'outline'}
        className="h-7"
        onClick={() => onSelect(null)}
      >
        <T context="filter chip for all kernel telemetry categories">
          All <Var>{total}</Var>
        </T>
      </Button>
      {categories.map((category) => {
        const count = summary?.byCategory[category] ?? 0;
        return (
          <Button
            key={category}
            type="button"
            size="sm"
            variant={selected === category ? 'secondary' : 'outline'}
            className="h-7 capitalize"
            onClick={() => onSelect(category)}
          >
            {category} {count}
          </Button>
        );
      })}
    </div>
  );
}

function KernelTelemetryEventRow({
  envelope,
  expanded,
  onToggle,
}: {
  envelope: KernelTelemetryEnvelope;
  expanded: boolean;
  onToggle: () => void;
}) {
  const gt = useGT();
  const time = kernelTelemetryEventTime(envelope);
  const detail = kernelTelemetryEventDetail(envelope);
  const timeLabel = time
    ? time.toLocaleTimeString(undefined, {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    : `#${envelope.seq}`;
  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          'flex w-full flex-wrap items-baseline gap-x-2 gap-y-0.5 px-2 py-1.5 text-left text-xs',
          expanded && 'bg-muted/50',
        )}
      >
        <span className="font-mono text-muted-foreground">{timeLabel}</span>
        <span className="capitalize">{envelope.event.category}</span>
        <span>{envelope.event.type}</span>
        {detail && <span className="text-muted-foreground">{detail}</span>}
        <span className="ml-auto text-muted-foreground">
          {expanded
            ? gt('hide', { context: 'collapse kernel telemetry event payload' })
            : gt('show', { context: 'expand kernel telemetry event payload' })}
        </span>
      </button>
      {expanded && (
        <pre className="overflow-x-auto bg-muted/40 px-2 py-2 font-mono text-[11px]">
          {JSON.stringify(envelope, null, 2)}
        </pre>
      )}
    </li>
  );
}
